import React, { useState, useRef, useCallback } from 'react';
import ReactGA from 'react-ga4';
import styled from 'styled-components';
import emailjs from '@emailjs/browser';
import { emailConfig, validationPatterns, errorMessages, successMessages, isEmailJSConfigured } from '../../config/emailConfig';

// Modal Overlay
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(5px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
  visibility: ${({ isOpen }) => (isOpen ? 'visible' : 'hidden')};
  transition: all 0.3s ease-in-out;
`;

// Modal Container
const ModalContainer = styled.div`
  background: ${({ theme }) => theme.card || '#1C1E27'};
  border-radius: 20px;
  padding: 1.5rem 1.75rem;
  width: 90%;
  max-width: 480px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  transform: ${({ isOpen }) => (isOpen ? 'scale(1)' : 'scale(0.8)')};
  transition: transform 0.3s ease-in-out;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);

  @media (max-width: 768px) {
    padding: 1.25rem;
    margin: 1rem;
  }
`;

// Close Button
const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  font-size: 1.5rem;
  color: ${({ theme }) => theme.text_primary || '#F2F3F4'};
  cursor: pointer;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;

  &:hover {
    background: ${({ theme }) => theme.primary || '#854CE6'};
    color: white;
    transform: rotate(90deg);
  }
`;

// Modal Header
const ModalHeader = styled.div`
  text-align: center;
  margin-bottom: 1rem;
`;

const ModalTitle = styled.h2`
  color: ${({ theme }) => theme.text_primary || '#F2F3F4'};
  font-size: 1.6rem;
  font-weight: 600;
  margin-bottom: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
`;

const EmailIcon = styled.span`
  font-size: 1.4rem;
`;

// Form Styles
const ContactForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const Label = styled.label`
  color: ${({ theme }) => theme.text_primary || '#F2F3F4'};
  font-weight: 500;
  font-size: 0.85rem;
`;

const Input = styled.input`
  padding: 0.6rem 0.85rem;
  border: 1.5px solid ${({ theme, hasError }) =>
    hasError ? '#ff4757' : theme.text_secondary || '#b1b2b3'};
  border-radius: 8px;
  background: ${({ theme }) => theme.bgLight || '#1C1E27'};
  color: ${({ theme }) => theme.text_primary || '#F2F3F4'};
  font-size: 0.95rem;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.primary || '#854CE6'};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.primary || '#854CE6'}20;
  }

  &::placeholder {
    color: ${({ theme }) => theme.text_secondary || '#b1b2b3'};
  }
`;

const TextArea = styled.textarea`
  padding: 0.6rem 0.85rem;
  border: 1.5px solid ${({ theme, hasError }) =>
    hasError ? '#ff4757' : theme.text_secondary || '#b1b2b3'};
  border-radius: 8px;
  background: ${({ theme }) => theme.bgLight || '#1C1E27'};
  color: ${({ theme }) => theme.text_primary || '#F2F3F4'};
  font-size: 0.95rem;
  min-height: 85px;
  resize: vertical;
  font-family: inherit;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.primary || '#854CE6'};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.primary || '#854CE6'}20;
  }

  &::placeholder {
    color: ${({ theme }) => theme.text_secondary || '#b1b2b3'};
  }
`;

const ErrorMessage = styled.span`
  color: #ff4757;
  font-size: 0.75rem;
  margin-top: 0.15rem;
`;

const SubmitButton = styled.button`
  background: linear-gradient(135deg, ${({ theme }) => theme.primary || '#854CE6'}, ${({ theme }) => theme.primary || '#854CE6'}DD);
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 0.4rem;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px ${({ theme }) => theme.primary || '#854CE6'}40;
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
  }
`;

const LoadingSpinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid transparent;
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const SuccessMessage = styled.div`
  background: #2ed573;
  color: white;
  padding: 1rem;
  border-radius: 8px;
  text-align: center;
  font-weight: 500;
  margin-bottom: 1rem;
`;



const ContactModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const formRef = useRef();
  const hasTrackedOpen = useRef(false);

  // Track modal open as GA4 event
  React.useEffect(() => {
    if (isOpen && !hasTrackedOpen.current) {
      ReactGA.event({ category: 'Contact', action: 'contact_modal_opened' });
      hasTrackedOpen.current = true;
    }
    if (!isOpen) {
      hasTrackedOpen.current = false;
    }
  }, [isOpen]);

  // Get validation patterns from config
  const { email: emailRegex, name: nameRegex, subject: subjectRegex, message: messageRegex } = validationPatterns;

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Enhanced form validation
  const validateForm = () => {
    const newErrors = {};

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = errorMessages.name.required;
    } else if (!nameRegex.test(formData.name.trim())) {
      newErrors.name = errorMessages.name.invalid;
    }

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = errorMessages.email.required;
    } else if (!emailRegex.test(formData.email.trim())) {
      newErrors.email = errorMessages.email.invalid;
    }

    // Subject validation
    if (!formData.subject.trim()) {
      newErrors.subject = errorMessages.subject.required;
    } else if (!subjectRegex.test(formData.subject.trim())) {
      newErrors.subject = errorMessages.subject.invalid;
    }

    // Message validation
    if (!formData.message.trim()) {
      newErrors.message = errorMessages.message.required;
    } else if (!messageRegex.test(formData.message.trim())) {
      newErrors.message = errorMessages.message.invalid;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }



    // Check if EmailJS is configured
    if (!isEmailJSConfigured()) {
      alert(`🚀 EmailJS Setup Required!\n\n` +
            `Quick 2-minute setup:\n` +
            `1. Go to https://emailjs.com\n` +
            `2. Sign up with urvanshshah9@gmail.com\n` +
            `3. Connect Gmail service\n` +
            `4. Create email template\n` +
            `5. Update src/config/emailConfig.js\n\n` +
            `See IMMEDIATE_FIX.md for detailed steps!\n\n` +
            `Or contact directly: ${emailConfig.toEmail}`);
      return;
    }

    setIsLoading(true);

    try {
      // Initialize EmailJS with public key
      emailjs.init(emailConfig.publicKey);

      // Send email using EmailJS
      const result = await emailjs.send(
        emailConfig.serviceId,
        emailConfig.templateId,
        {
          from_name: formData.name.trim(),
          from_email: formData.email.trim(),
          subject: formData.subject.trim(),
          message: formData.message.trim(),
          to_email: emailConfig.toEmail,
          reply_to: formData.email.trim()
        }
      );

      console.log('Email sent successfully:', result);
      ReactGA.event({ category: 'Contact', action: 'contact_form_submitted', label: formData.subject });
      setIsSuccess(true);
      setFormData({ name: '', email: '', subject: '', message: '' });

      // Auto close after 3 seconds
      setTimeout(() => {
        setIsSuccess(false);
        onClose();
      }, 3000);

    } catch (error) {
      console.error('Email sending failed:', error);

      // Show helpful error message
      const errorMsg = error?.text || error?.message || 'Unknown error';
      alert(`Email sending failed: ${errorMsg}\n\n` +
            `Please check your EmailJS configuration or\n` +
            `contact directly: ${emailConfig.toEmail}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle modal close
  const handleClose = useCallback(() => {
    if (!isLoading) {
      setFormData({ name: '', email: '', subject: '', message: '' });
      setErrors({});
      setIsSuccess(false);
      onClose();
    }
  }, [isLoading, onClose]);

  // Handle overlay click
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  // Handle escape key
  React.useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, handleClose]);

  return (
    <ModalOverlay isOpen={isOpen} onClick={handleOverlayClick}>
      <ModalContainer isOpen={isOpen}>
        <CloseButton onClick={handleClose} disabled={isLoading}>
          ×
        </CloseButton>
        
        <ModalHeader>
          <ModalTitle>
            <EmailIcon>📧</EmailIcon>
            Contact Me
          </ModalTitle>
        </ModalHeader>

        {isSuccess && (
          <SuccessMessage>
            {successMessages.emailSent}
          </SuccessMessage>
        )}

        <ContactForm ref={formRef} onSubmit={handleSubmit}>
          <FormGroup>
            <Label htmlFor="name">Your Name</Label>
            <Input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Enter your name"
              hasError={!!errors.name}
              disabled={isLoading}
            />
            {errors.name && <ErrorMessage>{errors.name}</ErrorMessage>}
          </FormGroup>

          <FormGroup>
            <Label htmlFor="email">Your Email</Label>
            <Input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Enter your email"
              hasError={!!errors.email}
              disabled={isLoading}
            />
            {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
          </FormGroup>

          <FormGroup>
            <Label htmlFor="subject">Subject</Label>
            <Input
              type="text"
              id="subject"
              name="subject"
              value={formData.subject}
              onChange={handleChange}
              placeholder="What's this about?"
              hasError={!!errors.subject}
              disabled={isLoading}
            />
            {errors.subject && <ErrorMessage>{errors.subject}</ErrorMessage>}
          </FormGroup>

          <FormGroup>
            <Label htmlFor="message">Your Message</Label>
            <TextArea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              placeholder="Tell me about your project or inquiry..."
              hasError={!!errors.message}
              disabled={isLoading}
            />
            {errors.message && <ErrorMessage>{errors.message}</ErrorMessage>}
          </FormGroup>

          <SubmitButton type="submit" disabled={isLoading}>
            {isLoading ? (
              <>
                <LoadingSpinner />
                Sending...
              </>
            ) : (
              <>
                📤 Send Message
              </>
            )}
          </SubmitButton>
        </ContactForm>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default ContactModal;
